import { LOCALE_LABELS, LOCALES, Wordmark } from "@elyxeer/ui";
import LogoutIcon from "@mui/icons-material/Logout";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, Outlet, useLocation } from "react-router-dom";

import {
  activeAdminPath,
  ADMIN_NAV_ITEMS,
} from "~/components/shell/admin-navigation";
import { asLocale, useLocale } from "~/i18n";
import { useAuthStore } from "~/store/auth-store";

/**
 * The panel's chrome: account controls followed by the primary work areas. The
 * selected tab comes from the URL, so detail routes and browser navigation stay honest.
 */
export function AdminShell() {
  const { t } = useTranslation();
  const { locale, setLocale } = useLocale();
  const user = useAuthStore((state) => state.user);
  const signOut = useAuthStore((state) => state.signOut);
  const location = useLocation();

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        // The solid content surface; the page gradient stays behind the sign-in only.
        backgroundColor: "background.paper",
      }}
    >
      <Box
        component="header"
        sx={{
          display: "flex",
          flexDirection: "column",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, md: 2 },
            width: "100%",
            paddingInline: { xs: 2.5, md: 5 },
            paddingBlock: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Wordmark size={20} />
            <Typography
              component="span"
              variant="H2"
              sx={{
                color: "text.muted",
                display: { xs: "none", sm: "block" },
              }}
            >
              {t("shell.eyebrow")}
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }} />

          <TextField
            select
            size="small"
            value={locale}
            onChange={(event) => void setLocale(asLocale(event.target.value))}
            aria-label={t("shell.language")}
            slotProps={{ htmlInput: { "data-testid": "shell.language" } }}
            sx={{
              width: { xs: 112, sm: 132 },
              "& .MuiFilledInput-root": { height: 48 },
              "& .MuiSelect-select": {
                height: "100% !important",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                padding: "0 40px 0 16px !important",
              },
              "& .MuiSelect-icon": { right: 12, fontSize: 20 },
            }}
          >
            {LOCALES.map((option) => (
              <MenuItem key={option} value={option}>
                {LOCALE_LABELS[option]}
              </MenuItem>
            ))}
          </TextField>

          <Typography
            variant="T2"
            sx={{
              color: "text.secondary",
              display: { xs: "none", md: "block" },
              maxWidth: { md: 220, lg: 320 },
            }}
            noWrap
          >
            {user?.email}
          </Typography>

          <Tooltip title={t("shell.signOut")}>
            <IconButton
              size="small"
              onClick={() => void signOut()}
              aria-label={t("shell.signOut")}
              data-testid="shell.sign-out"
              sx={{ width: 40, height: 40 }}
            >
              <LogoutIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Tabs
          component="nav"
          aria-label={t("shell.navigation.label")}
          value={activeAdminPath(location.pathname)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            width: "100%",
            paddingInline: { xs: 1.5, md: 4 },
            minHeight: 44,
          }}
        >
          {ADMIN_NAV_ITEMS.map((item) => (
            <Tab
              key={item.path}
              component={RouterLink}
              to={item.path}
              value={item.path}
              label={t(item.labelKey)}
              data-testid={`shell.nav-${item.testId}`}
              sx={{ minHeight: 44 }}
            />
          ))}
        </Tabs>
      </Box>

      <Box component="main" sx={{ flex: 1 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
